import type { AxiosRequestConfig } from "axios"

import axios from "axios"
import dayjs from "dayjs"
import { toast } from "sonner"
import { paths } from "@/src/routes/paths"
import { CONFIG } from "@/src/config-global"
import { showBadInternetToast, dismissBadInternetToast } from "@/src/common/badInternetToast/badInternetToast"

import apiUrls from "./api-urls"

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------

const REQUEST_RETRY_HEADER = "Pph-Token-Retry"
const REQUEST_API_TIMEOUT_RETRY_HEADER = "Pph-Token-Api-Timeout-Retry"

/** API URLs that are public (candidate-facing) — don't send recruiter JWT, don't redirect on 401 */
const isPublicUrl = (url: string | undefined): boolean =>
    !!url &&
    (url.includes("/public/") ||
        url.includes("/public-leads/") ||
        url.includes("/auth/v2/partner-candidate/validate-hash") ||
        url.includes("/auth/v2/partner-candidate/send-otp") ||
        url.includes("/auth/v2/partner-candidate/verify-otp"))

/** Candidate apply + ATS partner interview entry: never redirect to recruiter login on 401 */
const isCandidateApplyPage = (): boolean => {
    if (typeof window === "undefined") return false
    const path = window.location.pathname.replace(/\/$/, "")
    if (
        path.startsWith("/dashboard/jobDetails") ||
        path.startsWith("/dashboard/jobDetailsV2") ||
        path.startsWith("/dashboard/reschedule")
    ) {
        return true
    }
    if (path === "/interview" || path.startsWith("/interview/")) {
        return true
    }
    return false
}

// ----------------------------------------------------------------------
// HTTP Client Setup
// ----------------------------------------------------------------------

export const httpClient = axios.create({
    baseURL: CONFIG.site.serverUrl,
})

const shouldPreserveRescheduleConfirmedDate = (url: string | undefined, key: string): boolean => {
    return !!url?.includes("/public/interview-scheduling/reschedule/") && key === "confirmedDate"
}

// ----------------------------------------------------------------------
// Request Interceptor
// ----------------------------------------------------------------------

httpClient.interceptors.request.use((req) => {
    // Public (candidate-facing) endpoints must not send recruiter JWT; otherwise
    // stale recruiter token can cause 401 and redirect candidates to recruiter login
    if (isPublicUrl(req.url)) {
        delete req.headers.Authorization
    }

    // Convert all top-level dates to UTC
    if (req.method === "post" || req.method === "put") {
        try {
            const dataJson = req.data
            Object.entries(dataJson).forEach(([key, value]) => {
                if (shouldPreserveRescheduleConfirmedDate(req.url, key)) {
                    return
                }
                if (key.toLowerCase().includes("date")) {
                    if (typeof value === "string" && dayjs(value).isValid()) {
                        dataJson[key] = dayjs(value).toISOString()
                    }
                }
            })
            req.data = dataJson
        } catch (e) {
            return req
        }
    }
    return req
})

// ----------------------------------------------------------------------
// Response Interceptor
// ----------------------------------------------------------------------

httpClient.interceptors.response.use(
    (response) => {
        dismissBadInternetToast()
        return response
    },
    async (error) => {
        const statusCode = error.response?.status
        const originalRequestConfig = error.config

        // Handle 401 - Token refresh
        if (statusCode === 401 && !originalRequestConfig.headers[REQUEST_RETRY_HEADER]) {
            const { url } = error.config
            if (url !== apiUrls.AUTH_SIGN_IN && url !== apiUrls.AUTH_SIGN_UP && url !== apiUrls.AUTH_REFRESH_TOKEN) {
                // Public API or candidate apply page: never try refresh or redirect (apply page is /dashboard/jobDetails/...)
                if (isPublicUrl(url) || isCandidateApplyPage()) {
                    dismissBadInternetToast()
                    return Promise.reject((error.response && error.response.data) || "Something went wrong!")
                }
                const newToken = await getTokenFromRefresh()
                if (newToken) {
                    originalRequestConfig.headers.Authorization = `Bearer ${newToken}`
                    originalRequestConfig.headers[REQUEST_RETRY_HEADER] = "true"
                    return httpClient.request(originalRequestConfig)
                }
                toast.error("Please sign in again")
                setTimeout(() => {
                    window.location.href = paths.auth.jwt.signIn
                }, 1000)
                dismissBadInternetToast()
                return Promise.reject((error.response && error.response.data) || "Something went wrong!")
            }
        }

        // Handle network errors with retry
        if (
            error.code !== "ERR_CANCELED" &&
            error.code !== "ECONNABORTED" &&
            error.code !== "ERR_NETWORK" &&
            error.config.url !== apiUrls.WHATSAPP_DISCONNECT &&
            error.config.url !== apiUrls.DB_SAVED_SEARCHES_LIST &&
            error.config.url !== apiUrls.CANDIDATES_PUBLIC_AI_STATUS &&
            error.config.url !== apiUrls.FILE_PARSE_RESUME_PUBLIC &&
            !error.config.url?.startsWith(apiUrls.OTP_VERIFY)
        ) {
            dismissBadInternetToast()
        } else if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
            if (!originalRequestConfig.headers[REQUEST_API_TIMEOUT_RETRY_HEADER]) {
                originalRequestConfig.headers[REQUEST_API_TIMEOUT_RETRY_HEADER] = "true"
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(httpClient.request(originalRequestConfig))
                    }, 2000)
                })
            }
            showBadInternetToast()
        }

        dismissBadInternetToast()
        return Promise.reject(
            new Error(
                error.response
                    ? JSON.stringify({ ...error.response.data, httpError: error })
                    : JSON.stringify({ message: "Something went wrong", httpError: error })
            )
        )
    }
)

// ----------------------------------------------------------------------
// Utility Functions
// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
    try {
        const [url, config] = Array.isArray(args) ? args : [args]

        const res = await httpClient.get(url, { ...config })

        return res.data
    } catch (error) {
        console.error("Failed to fetch:", error)
        throw error
    }
}

export function removeUserToken() {
    localStorage.removeItem("jwt_access_token")
    localStorage.removeItem("jwt_refresh_token")
    delete axios.defaults.headers.common.Authorization
    delete httpClient.defaults.headers.common.Authorization
}

export async function getTokenFromRefresh(): Promise<string | undefined> {
    const refreshToken = localStorage.getItem("jwt_refresh_token")
    if (!refreshToken) {
        removeUserToken()
        return undefined
    }

    try {
        const resp = await httpClient.post(
            apiUrls.AUTH_REFRESH_TOKEN,
            { refreshToken },
            {
                transformRequest: (data, headers) => {
                    headers["Content-Type"] = "application/json"
                    delete headers.Authorization
                    return JSON.stringify(data)
                },
            }
        )
        const accessToken = resp?.data?.data?.token
        const newRefreshToken = resp?.data?.data?.refreshToken

        if (accessToken) {
            localStorage.setItem("jwt_access_token", accessToken)
            httpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`
            if (newRefreshToken) localStorage.setItem("jwt_refresh_token", newRefreshToken)
            return accessToken
        }
        removeUserToken()
        return undefined
    } catch (error) {
        removeUserToken()
        return undefined
    }
}

export const isNetworkError = (error: any) => {
    if (error?.httpError) return error?.httpError?.code === "ECONNABORTED" || error?.httpError?.code === "ERR_NETWORK"
    return (
        error?.errorMessage?.clientMessage === "Network Error" ||
        error?.errorMessage?.clientMessage?.includes("timeout")
    )
}

// ----------------------------------------------------------------------
// Backward Compatible Exports
// ----------------------------------------------------------------------

// Re-export everything that was in axios.ts for backward compatibility
export { httpClient as default }
export { apiUrls as endpoints } // Alias for backward compatibility during transition
